/** @jsxImportSource @emotion/react */
import React, { useMemo } from "react";
import { Provider } from "react-redux";
import { Index } from "apps/ProgramFinder/Index";
import { CountriesById, appendCountriesById, appendMeasuresById, appendStationsByCountryIdAndId, appendTemplateNameAndSharedWith, appendUsersAndGroupsById, enrichCountries } from "model/InitialModel";
import { HelpContext } from "components/help-text/HelpText";
import { ApplicationContext, ApplicationContextBaseType } from "components/application-context/ApplicationContext";
import { HelpText, getApplicationDisplayName } from "Utilities";
import { Applications } from "constants/application";
import { getStore } from "apps/ProgramFinder/app/store";
import { Params, useLoaderData } from "react-router";
import { ProgramFinderSourceModel } from "./Model";
import { getSessionCredentials } from "contexts/UserContext";
import { useReferenceData } from "contexts/ReferenceDataContext";
import { helpTextCache } from "constants/helpTextCache";
import { TemplateOrHistory } from "model/Model";
import { fetchHelpTextData, getJson } from "helpers/api";

const applicationId = Applications.ProgramFinder;

export type ApplicationContextType = ApplicationContextBaseType
  &
{
  readonly countriesById: CountriesById;
};

type LoaderData = { id: number; helpText: HelpText; initialData: TemplateOrHistory<ProgramFinderSourceModel>; };

export const programFinderLoader = async ( { request, params }: { request: Request; params: Params<string>; } ): Promise<LoaderData | null> => {
  const { token, userId } = getSessionCredentials();
  if ( token === "" ) return null;

  const id = parseInt( params.id as string || "-1" );

  let helpText: HelpText = helpTextCache[ applicationId ];
  if ( !helpText ) {
    helpText = await fetchHelpTextData( { userId, token, applicationId } );
    helpTextCache[ applicationId ] = helpText;
  }

  const initialData = await getJson<TemplateOrHistory<ProgramFinderSourceModel>>( "/Apps/ProgramFinder", userId, token, { id } );

  validateSourceModel( initialData.query );

  return { id, helpText, initialData };
};

const validateSourceModel = ( initialData: ProgramFinderSourceModel ) => {
  if ( !initialData ) throw new Error( "Source data has not been loaded" );

  if ( !initialData.title ) throw new Error( "SourceData.title has not been loaded" );
  if ( initialData.title.compareOperator === undefined ) throw new Error( "SourceData.title.compareOperator has not been loaded" );
  if ( initialData.title.numberOfCharactersToCompare === undefined ) throw new Error( "SourceData.title.numberOfCharactersToCompare has not been loaded" );
  if ( initialData.title.originalOrLocal === undefined ) throw new Error( "SourceData.title.originalOrLocal has not been loaded" );
  if ( initialData.title.titleNames === undefined ) throw new Error( "SourceData.title.titleNames has not been loaded" );
  if ( !Array.isArray( initialData.title.titleNames ) ) throw new Error( "SourceData.title.titleNames has not been loaded" );
  if ( initialData.title.searchTerm === undefined ) throw new Error( "SourceData.title.searchTerm has not been loaded" );

  if ( initialData.selectedCountries === undefined ) throw new Error( "SourceData.selectedCountries has not been loaded" );
  if ( !Array.isArray( initialData.selectedCountries ) ) throw new Error( "SourceData.selectedCountries has not been loaded" );

  if ( initialData.output === undefined ) throw new Error( "SourceData.output hasn't loaded correctly" );
  if ( initialData.output.format === undefined ) throw new Error( "SourceData.output.format hasn't loaded correctly" );
  if ( initialData.output.view === undefined ) throw new Error( "SourceData.output.view hasn't loaded correctly" );
};

export const ProgramFinder = () => {
  const loaderData = useLoaderData() as LoaderData;
  const referenceData = useReferenceData();
  const store = getStore( loaderData.initialData.query );

  const applicationContext: ApplicationContextType = useMemo( () =>
    appendTemplateNameAndSharedWith(
      loaderData.initialData.userId,
      loaderData.initialData.name,
      loaderData.initialData.sharedWith,
      appendCountriesById(
        appendUsersAndGroupsById(
          appendStationsByCountryIdAndId(
            appendMeasuresById(
              {
                applicationId,
                templateOrHistoryId: loaderData.id,
                countryGroups: referenceData.countryGroups,
                sharingUsersAndGroups: referenceData.sharingUsersAndGroups,
                includeIdInName: referenceData.includeIdInName,
                measures: referenceData.measures,
                countries: enrichCountries( referenceData.countries ),
                name: getApplicationDisplayName( applicationId )
              }
            )
          )
        )
      )
    ), [ loaderData.id, loaderData.initialData.name, loaderData.initialData.sharedWith, loaderData.initialData.userId, referenceData.countries, referenceData.countryGroups, referenceData.includeIdInName, referenceData.measures, referenceData.sharingUsersAndGroups ] );

  return (
    <ApplicationContext.Provider value={ applicationContext } key={ loaderData.id }>
      <HelpContext.Provider value={ loaderData.helpText } >
        <Provider store={ store }>
          <Index />
        </Provider>
      </HelpContext.Provider>
    </ApplicationContext.Provider>
  );
};